import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api from './api';

const ThemeContext = createContext(null);
const TOKEN_KEY = 'cr_access_token';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = useCallback(async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await api.get('/theme');
      const themeData = response.data;
      
      // Apply theme to CSS variables
      applyTheme(themeData);
      setTheme(themeData);
    } catch (error) {
      console.error('[Theme] Failed to load theme:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyTheme = (themeData) => {
    const root = document.documentElement;
    const inventoryModule = themeData.module_overrides?.inventory || {};
    
    // Apply base colors
    if (themeData.base_colors) {
      root.style.setProperty('--background', themeData.base_colors.background);
      root.style.setProperty('--surface', themeData.base_colors.surface);
      root.style.setProperty('--border', themeData.base_colors.border);
    }
    
    // Apply inventory module overrides
    if (inventoryModule.accent_color) {
      root.style.setProperty('--accent-color', inventoryModule.accent_color);
    }
    if (inventoryModule.alert_high) {
      root.style.setProperty('--alert-high', inventoryModule.alert_high);
    }
    if (inventoryModule.alert_medium) {
      root.style.setProperty('--alert-medium', inventoryModule.alert_medium);
    }
    if (inventoryModule.alert_low) {
      root.style.setProperty('--alert-low', inventoryModule.alert_low);
    }
  };

  const themeContextValue = useMemo(() => ({ theme, loading, loadTheme }), [theme, loading, loadTheme]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
